import React from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import IucnSpeciesChart from 'src/shared/components/iucn-species-chart/IucnSpeciesChart';
import InvasiveSpeciesIcon from 'src/shared/components/invasive-species-icon/InvasiveSpeciesIcon';
import IucnLogo from 'src/shared/components/iucn-logo';
import Loader from 'src/shared/components/loader/Loader';
import SiteSpeciesRiskModal from 'src/app/portfolio/views/portfolio-site-explorer/components/site-species-risk-modal/SiteSpeciesRiskModal';
import { useSiteSpeciesCountsQuery } from '../state/api/siteSummaryGraphSlice';
import useSiteSummaryAnalytics from '../hooks/useSiteSummaryAnalytics';
import styles from '../SiteSummary.module.scss';

type Props = {
    siteId: string;
};

/**
 * The site's species risk section, read from the global prediction grid.
 */
const SiteSpeciesRiskIndicators = ({ siteId }: Props) => {
    const { currentData, isFetching } = useSiteSpeciesCountsQuery({ siteId }, { skip: !siteId });
    const { trackExploreButtonClick } = useSiteSummaryAnalytics();
    const [modalTab, setModalTab] = React.useState<null | 'iucn' | 'invasives'>(null);

    // The section names predate the grid, so analytics dashboards keep reading.
    const exploreInto = (section: string, tab: 'iucn' | 'invasives') => {
        trackExploreButtonClick({ section });
        setModalTab(tab);
    };

    // Recharts' datum key, not the grid's: IucnSpeciesChart names the category
    // iucnRedListCategory.
    const chartData = (currentData?.iucn ?? []).map(entry => ({
        iucnRedListCategory: entry.category,
        count: entry.count,
    }));

    return (
        <div className='flex flex-col gap-3' data-testid='site-species'>
            <div className={`text-xl text-black ${styles.sectionTitle}`}>Species risk indicators</div>
            {isFetching || !currentData ? (
                <div className='flex justify-center py-10'>
                    <Loader />
                </div>
            ) : (
                <>
                    <div className='flex gap-3 items-stretch'>
                        <div className='w-1/4' data-testid='site-species-invasive'>
                            <SummaryCard
                                containerClasses={`gap-3 p-5 h-full ${styles.invasiveSpeciesCard}`}
                                cardIcon={
                                    <div className={styles.iconGradient}>
                                        <InvasiveSpeciesIcon />
                                    </div>
                                }
                                cardTitle='Invasive Species'
                                cardSubtitle='Predicted invasive species overlap across site'
                                cardBody={
                                    <div className='flex flex-col gap-1'>
                                        <div className='text-[42px] text-grey-80'>{currentData.invasiveSpeciesCount}</div>
                                        <div className='text-sm text-grey-100'>Total invasive species</div>
                                    </div>
                                }
                                action={
                                    <ExploreButton
                                        sectionName='invasive-species'
                                        onClick={() => exploreInto('species_risk_invasive_species', 'invasives')}
                                    />
                                }
                            />
                        </div>

                        <div className='w-3/4' data-testid='site-species-red-list'>
                            <SummaryCard
                                containerClasses={`gap-3 p-5 h-full ${styles.iucnCard}`}
                                cardIcon={
                                    <div className={styles.iconGradient}>
                                        <IucnLogo />
                                    </div>
                                }
                                cardTitle='IUCN Red List species'
                                cardSubtitle='Predicted red list species overlap across site'
                                cardBody={
                                    <div className='flex gap-4'>
                                        <div className='flex flex-col'>
                                            <span className='text-[42px] text-grey-80'>{currentData.redListSpeciesCount}</span>
                                            <div className='text-sm text-grey-80'>Total red list species</div>
                                            {/* The narrower CR/EN/VU subset of the Red List total. */}
                                            <div className='text-sm text-grey-100'>
                                                {currentData.threatenedSpeciesCount} threatened (CR, EN, VU)
                                            </div>
                                        </div>

                                        <div className='bg-grey-20 w-[1px]'>&nbsp;</div>

                                        <IucnSpeciesChart className='flex' chartData={chartData} />
                                    </div>
                                }
                                action={
                                    <ExploreButton
                                        sectionName='iucn-red-list-species'
                                        onClick={() => exploreInto('species_risk_iucn_red_list_species', 'iucn')}
                                    />
                                }
                            />
                        </div>
                    </div>

                    {modalTab && <SiteSpeciesRiskModal siteId={siteId} initialTab={modalTab} onClose={() => setModalTab(null)} />}
                </>
            )}
        </div>
    );
};

export default SiteSpeciesRiskIndicators;
